import { ChangeDetectorRef, Component, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { AutocompleteComponent } from "../../../../Common/Components/Autocomplete/AutocompleteComponent";
import { AutocompleteOption } from "../../../../Common/Components/Autocomplete/Type/AutocompleteTypes";
import { LocationSelectorComponent } from "../../../../Common/Components/LocationSelector/LocationSelectorComponent";
import { LocationAddress } from "../../../../Common/Components/LocationSelector/Type/LocationSelectorTypes";
import { TeamLeaderEffects } from "../../State/TeamLeaderEffect";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { TeamLeaderListItem } from "../../Dto/TeamLeaderModel";
import { PartnerApi } from "../../../Partner/Api/PartnerApi";
import { PartnerSearchRequest } from "../../../Partner/Dto/PartnerSearchRequest";
import { UpdateTeamLeaderRequest } from "../../Dto/TeamLeaderUpdateRequest";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-team-leader-edit",
  standalone: true,
  imports: [ReactiveFormsModule, AppButtonComponent, AppCardComponent, AutocompleteComponent, LocationSelectorComponent],
  templateUrl: "./TeamLeaderEditPage.html",
  styleUrl: "./TeamLeaderEditPage.scss",
})
export class TeamLeaderEditPage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly teamLeaderEffects = inject(TeamLeaderEffects);
  private readonly partnerApi = inject(PartnerApi);
  private readonly toastEffects = inject(ToastEffects);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly teamLeaderId = this.route.snapshot.paramMap.get("id") ?? undefined;

  teamLeaderForm: FormGroup;
  teamLeader: TeamLeaderListItem | null = null;
  isReady = false;
  isSaving = false;
  selectedPartnerIds: string[] = [];
  searchedPartners: AutocompleteOption[] = [];
  addressValue?: LocationAddress;

  constructor() {
    this.teamLeaderForm = this.fb.group({
      name: ["", [Validators.required, Validators.minLength(2)]],
      email: [""],
      phone: [""],
      address: this.fb.group({
        stateProvinceRegion: ["", [Validators.required]],
        countyDistrict: ["", [Validators.required]],
        subdistrictMunicipality: ["", [Validators.required]],
        communeVillage: ["", [Validators.required]],
        line: [""],
        zip: [""],
        longitude: [0],
        latitude: [0],
      }),
    });
  }

  ngOnInit(): void {
    if (!this.teamLeaderId) {
      this.router.navigate(["/team-leaders"]).catch(() => undefined);
      return;
    }

    this.fetchTeamLeader();
  }

  private async fetchTeamLeader(): Promise<void> {
    if (!this.teamLeaderId) {
      this.toastEffects.show("Team leader ID is required.", "error");
      await this.router.navigate(["/team-leaders"]);
      return;
    }

    const teamLeader = await this.teamLeaderEffects.getTeamLeaderById(this.teamLeaderId);
    if (!teamLeader) {
      this.toastEffects.show("Team leader not found.", "error");
      await this.router.navigate(["/team-leaders"]);
      return;
    }

    setTimeout(() => {
      this.teamLeader = teamLeader;
      this.patchFormFromTeamLeader(teamLeader);
      this.isReady = true;
      this.cdr.detectChanges();
    }, 0);
  }

  async searchPartners(term: string): Promise<void> {
    const params: PartnerSearchRequest = {
      search: term,
      page: 1,
      limit: 50,
    };

    const response = await this.partnerApi.searchPartners(params);
    const searchResults = response.data.map((inst) => ({
      value: inst.id,
      label: inst.name,
    }));

    const existingSelectedPartners = this.searchedPartners.filter((existing) =>
      this.selectedPartnerIds.includes(existing.value.toString()),
    );

    const existingIds = new Set(searchResults.map((inst) => inst.value.toString()));
    this.searchedPartners = [
      ...searchResults,
      ...existingSelectedPartners.filter((inst) => !existingIds.has(inst.value.toString())),
    ];
  }

  onPartnerChange(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) {
      this.selectedPartnerIds = value.map((v) => v.toString());
    }
  }

  async saveTeamLeader(): Promise<void> {
    if (this.teamLeaderForm.invalid) {
      this.teamLeaderForm.markAllAsTouched();
      this.toastEffects.show("Please fill in all required fields.", "error");
      return;
    }

    if (!this.teamLeaderId) {
      this.toastEffects.show("Missing team leader identifier.", "error");
      return;
    }

    this.isSaving = true;
    this.cdr.detectChanges();

    const formData = this.teamLeaderForm.value;

    try {
      const basicUpdateData: UpdateTeamLeaderRequest = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        address: {
          stateProvinceRegion: formData.address.stateProvinceRegion,
          countyDistrict: formData.address.countyDistrict,
          subdistrictMunicipality: formData.address.subdistrictMunicipality,
          communeVillage: formData.address.communeVillage,
          line: formData.address.line || "",
          zip: formData.address.zip || "",
          longitude: 0,
          latitude: 0,
        },
      };

      const updatedTeamLeader = await this.teamLeaderEffects.updateTeamLeader(this.teamLeaderId, basicUpdateData);

      if (this.selectedPartnerIds.length > 0) {
        const assignedTeamLeader = await this.teamLeaderEffects.assignPartners(this.teamLeaderId, this.selectedPartnerIds);

        setTimeout(() => {
          this.teamLeader = assignedTeamLeader;
          this.patchFormFromTeamLeader(assignedTeamLeader);
          this.toastEffects.show("Team leader updated successfully.", "success");
          this.cdr.detectChanges();
        }, 0);
      } else {
        setTimeout(() => {
          this.teamLeader = updatedTeamLeader;
          this.patchFormFromTeamLeader(updatedTeamLeader);
          this.toastEffects.show("Team leader updated successfully.", "success");
          this.cdr.detectChanges();
        }, 0);
      }
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to update team leader."), "error");
    } finally {
      setTimeout(() => {
        this.isSaving = false;
        this.cdr.detectChanges();
      }, 0);
    }
  }

  goBack(): void {
    this.router.navigate(["/team-leaders"]).catch(() => undefined);
  }

  onAddressChange(value: LocationAddress): void {
    this.addressValue = value;
    this.teamLeaderForm.patchValue({
      address: {
        stateProvinceRegion: value.stateProvinceRegion,
        countyDistrict: value.countyDistrict,
        subdistrictMunicipality: value.subdistrictMunicipality,
        communeVillage: value.communeVillage,
        line: value.line,
        zip: value.zip,
      },
    });
  }

  private patchFormFromTeamLeader(teamLeader: TeamLeaderListItem): void {
    if (this.teamLeaderForm.dirty) {
      return;
    }

    const partnerIds = teamLeader.partners?.map((inst) => inst.id) || [];
    this.selectedPartnerIds = partnerIds;
    this.addressValue = this.mapAddress(teamLeader);

    if (teamLeader.partners && teamLeader.partners.length > 0) {
      this.searchedPartners = teamLeader.partners.map((inst) => ({
        value: inst.id,
        label: inst.name,
      }));
    }

    this.teamLeaderForm.patchValue(
      {
        name: teamLeader.name,
        email: teamLeader.email || "",
        phone: teamLeader.phone || "",
        address: {
          stateProvinceRegion: this.addressValue.stateProvinceRegion,
          countyDistrict: this.addressValue.countyDistrict,
          subdistrictMunicipality: this.addressValue.subdistrictMunicipality,
          communeVillage: this.addressValue.communeVillage,
          line: this.addressValue.line || "",
          zip: this.addressValue.zip || "",
          longitude: this.addressValue.longitude || 0,
          latitude: this.addressValue.latitude || 0,
        },
      },
      { emitEvent: false },
    );
  }

  private mapAddress(teamLeader: TeamLeaderListItem): LocationAddress {
    return {
      stateProvinceRegion: teamLeader.address?.stateProvinceRegion ?? "",
      countyDistrict: teamLeader.address?.countyDistrict ?? "",
      subdistrictMunicipality: teamLeader.address?.subdistrictMunicipality ?? "",
      communeVillage: teamLeader.address?.communeVillage ?? "",
      line: teamLeader.address?.line ?? "",
      zip: teamLeader.address?.zip ?? "",
      longitude: teamLeader.address?.longitude ?? 0,
      latitude: teamLeader.address?.latitude ?? 0,
    };
  }
}
